import React from 'react';
import styles from "./PersonalInfo.module.css";
import SectionPanel from '../../../../components/section-panel/SectionPanel';
import CalendarConfig from "../../../../consts/CalendarConfig";
import PatientConfig from "../../../../consts/PatientConfig";
import CustomButton from "../../../../components/buttons/CustomButton";
import { GetFriendlyCPF, GetCurrentAge } from '../../../../utils/Utils';
import { formatPhone } from '../../../../utils/phoneUtils';
import EditIcon from '@mui/icons-material/Edit';
import { Alert } from '@mui/material';
import ModuleCard from '../../../appointment/components/moduleCard/ModuleCard';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { isOwner } from '../../../../utils/RoleManager';
import PatientPrivacyPanel from './PatientPrivacyPanel';

/** Valor que conta como preenchido ("0" é o padrão do cadastro para número e sexo). */
const hasValue = (value) => value != null && value !== '' && value !== '0' && value !== 0;

/** Linha do painel: rótulo à esquerda, valor à direita (link quando houver `href`). */
function Row({ label, value, hint, href, external }) {
    const text = (
        <>
            {value}
            {hint && <span className={styles.rowHint}> · {hint}</span>}
        </>
    );
    return (
        <div className={styles.row}>
            <span className={styles.rowLabel}>{label}</span>
            {href ? (
                <a
                    className={`${styles.rowValue} ${styles.rowLink}`}
                    href={href}
                    target={external ? '_blank' : undefined}
                    rel={external ? 'noopener noreferrer' : undefined}
                >
                    {text}
                </a>
            ) : (
                <span className={styles.rowValue}>{text}</span>
            )}
        </div>
    );
}

/**
 * Painel de um grupo do cadastro: ícone colorido, título, linhas preenchidas e, no
 * rodapé, o que ficou em branco com o atalho "Completar" (abre o cadastro).
 * Grupo sem nada preenchido mostra `emptyText` no lugar das linhas.
 */
function InfoPanel({ icon, title, subtitle, accent, rows, emptyText, onComplete, before, headerAction }) {
    const filled = rows.filter((row) => hasValue(row.value));
    const missing = rows.filter((row) => !hasValue(row.value)).map((row) => row.label);
    const showFooter = missing.length > 0 && (filled.length > 0 || onComplete);

    return (
        <SectionPanel icon={icon} title={title} subtitle={subtitle} accent={accent} aside={headerAction}>
            {before}
            {filled.length > 0 ? (
                <div className={styles.rows}>
                    {filled.map((row) => <Row key={row.label} {...row} />)}
                </div>
            ) : (
                <p className={styles.panelEmpty}>{emptyText}</p>
            )}
            {showFooter && (
                <div className={styles.panelFooter}>
                    {filled.length > 0 && (
                        <span className={styles.missing}>Em branco: {missing.join(', ')}</span>
                    )}
                    {onComplete && (
                        <button type="button" className={styles.completeButton} onClick={onComplete}>
                            {filled.length > 0 ? 'Completar' : 'Preencher'}
                        </button>
                    )}
                </div>
            )}
        </SectionPanel>
    );
}

/**
 * Seção Dados pessoais da ficha: card de cadastro com quatro painéis (identificação,
 * contato, endereço, responsável) e, para quem é proprietário(a) da conta, o card
 * "Privacidade e dados" com os direitos do titular (LGPD).
 */
const PersonalInfo = ({ data, onEdit, onChange }) => {

    const isAnonymized = !!data.anonymized_at;
    // isOwner() lê o DataProvider (hook): só no corpo do componente
    const canManagePrivacy = isOwner();
    const onComplete = isAnonymized ? null : onEdit;

    function formatDocument(doc) {
        if (!doc) return "";
        const digits = doc.replace(/\D/g, '');
        if (digits.length === 11) {
            return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        if (digits.length === 14) {
            return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return doc;
    }

    function getFriendlyDate() {
        return data.birthday ? CalendarConfig.GetFullDateName(data.birthday) : "";
    }

    /** Idade curta ("38 anos", "5 meses") para acompanhar a data de nascimento. */
    function getShortAge() {
        if (!data.birthday) return "";
        try {
            return CalendarConfig.GetAge(data.birthday).split(", ")[0];
        } catch (error) {
            return "";
        }
    }

    /** "Não informado" (id 99) conta como vazio. */
    function getGender() {
        if (!hasValue(data.gender) || String(data.gender) === "99") return "";
        const item = PatientConfig.GetGenderById(data.gender);
        return item ? item.Label : "";
    }

    function isMinor() {
        if (!data.birthday) return false;
        try {
            let day, month, year;
            if (data.birthday.includes("/")) {
                [day, month, year] = data.birthday.split("/");
            } else if (data.birthday.includes("-")) {
                [year, month, day] = data.birthday.split("-");
            } else {
                return false;
            }
            const birthDate = new Date(year, month - 1, day);
            if (isNaN(birthDate.getTime())) return false;
            return GetCurrentAge(birthDate) < 18;
        } catch (error) {
            return false;
        }
    }

    const streetAndNumber = [data.address, hasValue(data.number) ? data.number : ""].filter(Boolean).join(", ");
    const street = [streetAndNumber, data.complement].filter(Boolean).join(" · ");
    const cityState = [data.city, data.state].filter(Boolean).join(" / ");
    const mapQuery = [streetAndNumber, data.district, data.city, data.state].filter(Boolean).join(", ");
    const mapAction = mapQuery ? (
        <a
            className={styles.headerAction}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            Abrir no mapa
            <OpenInNewIcon />
        </a>
    ) : null;

    const responsiblePhone = data.financial_responsible_phone;
    const minorWithoutResponsible = isMinor() && !data.financial_responsible_name;

    const editAction = isAnonymized ? null : (
        <CustomButton
            label="Editar cadastro"
            variant="contained"
            style="secondary"
            icon={<EditIcon fontSize="small" />}
            onClick={onEdit}
        />
    );

    return (
        <>
            <ModuleCard
                title="Dados pessoais"
                icon={<PersonOutlineOutlinedIcon />}
                description="Cadastro, contato, endereço e responsável"
                accent="blue"
                actions={editAction}
            >
                {isAnonymized && (
                    <Alert severity="info">
                        Este paciente foi anonimizado e não pode mais ser editado.
                    </Alert>
                )}

                <div className={styles.panels}>
                    <InfoPanel
                        icon={<BadgeOutlinedIcon />}
                        title="Identificação"
                        subtitle={`Registro nº ${data.id}`}
                        accent="blue"
                        onComplete={onComplete}
                        emptyText="Nascimento, sexo biológico e CPF ainda não informados."
                        rows={[
                            { label: 'Nascimento', value: getFriendlyDate(), hint: getShortAge() },
                            { label: 'Sexo biológico', value: getGender() },
                            { label: 'CPF', value: GetFriendlyCPF(data.cpf) },
                        ]}
                    />

                    <InfoPanel
                        icon={<ContactPhoneOutlinedIcon />}
                        title="Contato"
                        subtitle="Telefone e e-mail"
                        accent="teal"
                        onComplete={onComplete}
                        emptyText="Nenhum telefone ou e-mail informado."
                        rows={[
                            { label: 'Telefone', value: data.phone ? formatPhone(data.phone) : "", href: data.phone ? `tel:${data.phone}` : null },
                            { label: 'E-mail', value: data.email, href: data.email ? `mailto:${data.email}` : null },
                        ]}
                    />

                    <InfoPanel
                        icon={<PlaceOutlinedIcon />}
                        title="Endereço"
                        subtitle="Onde mora"
                        accent="amber"
                        onComplete={onComplete}
                        headerAction={mapAction}
                        emptyText="Nenhum endereço informado."
                        rows={[
                            { label: 'Logradouro', value: street },
                            { label: 'Bairro', value: data.district },
                            { label: 'Cidade', value: cityState },
                            { label: 'CEP', value: data.cep },
                        ]}
                    />

                    <InfoPanel
                        icon={<FamilyRestroomOutlinedIcon />}
                        title="Responsável"
                        subtitle="Financeiro e legal"
                        accent="purple"
                        onComplete={onComplete}
                        emptyText="Sem responsável cadastrado. Obrigatório para menores de 18 anos."
                        before={minorWithoutResponsible && (
                            <Alert severity="warning">
                                Paciente menor de idade sem responsável cadastrado. Informe o responsável legal, que deve dar o consentimento para o tratamento dos dados do paciente (LGPD, art. 14).
                            </Alert>
                        )}
                        rows={[
                            { label: 'Nome', value: data.financial_responsible_name },
                            { label: 'Relação', value: data.financial_responsible_relationship },
                            { label: 'CPF / CNPJ', value: formatDocument(data.financial_responsible_document) },
                            { label: 'Telefone', value: responsiblePhone ? formatPhone(responsiblePhone) : "", href: responsiblePhone ? `tel:${responsiblePhone}` : null },
                            { label: 'E-mail', value: data.financial_responsible_email, href: data.financial_responsible_email ? `mailto:${data.financial_responsible_email}` : null },
                        ]}
                    />
                </div>
            </ModuleCard>

            {canManagePrivacy && <PatientPrivacyPanel patient={data} onChange={onChange} />}
        </>
    );
};

export default PersonalInfo;
